/**
 * Customer Overview Component (T074)
 * Contact info, tags, health score, quick actions, and stats
 */

import { useState } from 'react';
import {
  Mail,
  Phone,
  MapPin,
  Calendar,
  FileText,
  CreditCard,
  Edit,
  Archive,
  RotateCcw,
  Plus,
  Tag,
  X,
  Heart,
  Globe,
  Send,
  CheckCircle2,
  Clock,
  AlertCircle,
} from 'lucide-react';
import {
  type CustomerProfile,
  useUpdateCustomerTags,
  useArchiveCustomer,
  useRestoreCustomer,
} from '../../hooks/useCustomers';
import { usePortalStatus, useSendPortalInvitation } from '../../hooks/useCustomers';

interface CustomerOverviewProps {
  customer: CustomerProfile;
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

function HealthScoreIndicator({ score }: { score: number }) {
  const color = score > 70 ? 'text-success' : score > 40 ? 'text-warning' : 'text-destructive';
  const bgColor = score > 70 ? 'bg-success' : score > 40 ? 'bg-warning' : 'bg-destructive';
  const label = score > 70 ? 'Excellent' : score > 40 ? 'Fair' : 'At Risk';

  const circumference = 2 * Math.PI * 40;
  const dashOffset = circumference - (score / 100) * circumference;

  return (
    <div className="flex items-center gap-4">
      <div className="relative w-24 h-24">
        <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="40" fill="none" strokeWidth="8" className="stroke-muted" />
          <circle
            cx="50"
            cy="50"
            r="40"
            fill="none"
            strokeWidth="8"
            strokeLinecap="round"
            className={`${bgColor.replace('bg-', 'stroke-')}`}
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-xl font-bold ${color}`}>{score}</span>
        </div>
      </div>
      <div>
        <p className={`font-semibold ${color}`}>{label}</p>
        <p className="text-sm text-muted-foreground">Health Score</p>
      </div>
    </div>
  );
}

function HealthBreakdown({
  breakdown,
}: {
  breakdown: CustomerProfile['healthScore']['breakdown'];
}) {
  const items = [
    { label: 'Recency', value: breakdown.recency },
    { label: 'Frequency', value: breakdown.frequency },
    { label: 'Payment', value: breakdown.payment },
    { label: 'Engagement', value: breakdown.engagement },
  ];

  return (
    <div className="space-y-3 mt-4">
      {items.map((item) => (
        <div key={item.label}>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-muted-foreground">{item.label}</span>
            <span className="font-medium">{item.value}%</span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                item.value > 70 ? 'bg-success' : item.value > 40 ? 'bg-warning' : 'bg-destructive'
              }`}
              style={{ width: `${item.value}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CustomerOverview({ customer }: CustomerOverviewProps) {
  const [newTag, setNewTag] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);

  const updateTags = useUpdateCustomerTags();
  const archiveCustomer = useArchiveCustomer();
  const restoreCustomer = useRestoreCustomer();
  const { data: portalStatus } = usePortalStatus(customer.id);
  const sendInvitation = useSendPortalInvitation();

  const handleAddTag = () => {
    const tag = newTag.trim();
    if (tag && !customer.tags.includes(tag)) {
      updateTags.mutate({ id: customer.id, tags: [...customer.tags, tag] });
    }
    setNewTag('');
    setShowTagInput(false);
  };

  const handleRemoveTag = (tagToRemove: string) => {
    updateTags.mutate({
      id: customer.id,
      tags: customer.tags.filter((t) => t !== tagToRemove),
    });
  };

  const handleArchiveToggle = () => {
    if (customer.archived) {
      restoreCustomer.mutate(customer.id);
    } else {
      archiveCustomer.mutate(customer.id);
    }
  };

  const counts = customer.counts || {
    appointments: customer.appointmentsCount,
    invoices: customer.invoicesCount,
    payments: customer.paymentsCount || 0,
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Contact Information */}
      <div className="lg:col-span-2 space-y-6">
        <div className="claude-card p-6">
          <div className="flex items-start gap-5">
            <div className="w-20 h-20 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-bold text-2xl">{getInitials(customer.name)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-2xl font-semibold truncate">{customer.name}</h2>
                {customer.archived && (
                  <span className="claude-badge claude-badge-warning">Archived</span>
                )}
                {customer.taxExempt && (
                  <span className="claude-badge claude-badge-default">Tax Exempt</span>
                )}
              </div>
              <p className="text-muted-foreground">
                Customer since {formatDate(customer.createdAt)}
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-muted-foreground text-xs">Email</p>
                <p className="truncate">{customer.email}</p>
              </div>
            </div>

            {customer.phone && (
              <div className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Phone</p>
                  <p>{customer.phone}</p>
                </div>
              </div>
            )}

            {(customer.address || customer.city) && (
              <div className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-muted-foreground text-xs">Address</p>
                  <p className="truncate">
                    {[customer.address, customer.city, customer.state, customer.zipCode]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                </div>
              </div>
            )}

            {customer.dateOfBirth && (
              <div className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Date of Birth</p>
                  <p>{formatDate(customer.dateOfBirth)}</p>
                </div>
              </div>
            )}
          </div>

          {customer.notes && (
            <div className="mt-6 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1 font-medium">Notes</p>
              <p className="text-sm whitespace-pre-wrap">{customer.notes}</p>
            </div>
          )}
        </div>

        {/* Tags */}
        <div className="claude-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Tag className="w-5 h-5 text-muted-foreground" />
              Tags
            </h3>
            <button
              onClick={() => setShowTagInput(!showTagInput)}
              className="claude-button claude-button-ghost text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Tag
            </button>
          </div>

          {showTagInput && (
            <div className="flex items-center gap-2 mb-4">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                placeholder="Enter tag name..."
                className="claude-input flex-1"
                autoFocus
              />
              <button
                onClick={handleAddTag}
                className="claude-button claude-button-primary text-sm"
              >
                Add
              </button>
              <button
                onClick={() => {
                  setShowTagInput(false);
                  setNewTag('');
                }}
                className="claude-button claude-button-ghost text-sm"
              >
                Cancel
              </button>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {(customer.tags || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No tags added yet</p>
            ) : (
              customer.tags.map((tag) => (
                <span
                  key={tag}
                  className="claude-badge claude-badge-default flex items-center gap-1.5 pr-1.5"
                >
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="hover:text-destructive transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="claude-card p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{counts.appointments}</p>
                <p className="text-sm text-muted-foreground">Appointments</p>
              </div>
            </div>
          </div>

          <div className="claude-card p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{counts.invoices}</p>
                <p className="text-sm text-muted-foreground">Invoices</p>
              </div>
            </div>
          </div>

          <div className="claude-card p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{counts.payments}</p>
                <p className="text-sm text-muted-foreground">Payments</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Sidebar */}
      <div className="space-y-6">
        {/* Health Score */}
        <div className="claude-card p-6">
          <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Heart className="w-5 h-5 text-muted-foreground" />
            Health Score
          </h3>
          {customer.healthScore ? (
            <>
              <HealthScoreIndicator score={customer.healthScore.score} />
              <HealthBreakdown breakdown={customer.healthScore.breakdown} />
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Health score not available</p>
          )}
        </div>

        {/* Quick Actions */}
        <div className="claude-card p-6">
          <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <button className="claude-button claude-button-secondary w-full justify-start gap-2 text-sm">
              <Edit className="w-4 h-4" />
              Edit Customer
            </button>
            <a
              href={`mailto:${customer.email}`}
              className="claude-button claude-button-secondary w-full justify-start gap-2 text-sm"
            >
              <Mail className="w-4 h-4" />
              Send Email
            </a>
            <button className="claude-button claude-button-secondary w-full justify-start gap-2 text-sm">
              <FileText className="w-4 h-4" />
              New Invoice
            </button>
            <button
              onClick={handleArchiveToggle}
              disabled={archiveCustomer.isPending || restoreCustomer.isPending}
              className={`claude-button w-full justify-start gap-2 text-sm ${
                customer.archived ? 'claude-button-secondary' : 'claude-button-destructive'
              }`}
            >
              {customer.archived ? (
                <>
                  <RotateCcw className="w-4 h-4" />
                  Restore Customer
                </>
              ) : (
                <>
                  <Archive className="w-4 h-4" />
                  Archive Customer
                </>
              )}
            </button>
          </div>
        </div>

        {/* Portal Access Status (T146) */}
        <div className="claude-card p-6">
          <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5 text-muted-foreground" />
            Portal Access
          </h3>
          {portalStatus ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {portalStatus.status === 'ACTIVE' && (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-success" />
                    <span className="claude-badge claude-badge-success">Active</span>
                  </>
                )}
                {portalStatus.status === 'INVITATION_PENDING' && (
                  <>
                    <Clock className="w-4 h-4 text-warning" />
                    <span className="claude-badge claude-badge-warning">Invitation Pending</span>
                  </>
                )}
                {portalStatus.status === 'DEACTIVATED' && (
                  <>
                    <AlertCircle className="w-4 h-4 text-destructive" />
                    <span className="claude-badge claude-badge-destructive">Deactivated</span>
                  </>
                )}
                {portalStatus.status === 'NOT_INVITED' && (
                  <>
                    <AlertCircle className="w-4 h-4 text-muted-foreground" />
                    <span className="claude-badge claude-badge-default">Not Invited</span>
                  </>
                )}
              </div>

              {portalStatus.portalUser && (
                <div className="text-sm space-y-1.5 text-muted-foreground">
                  {portalStatus.portalUser.invitationSentAt && (
                    <p>Invited: {formatDate(portalStatus.portalUser.invitationSentAt)}</p>
                  )}
                  {portalStatus.portalUser.invitationAcceptedAt && (
                    <p>Activated: {formatDate(portalStatus.portalUser.invitationAcceptedAt)}</p>
                  )}
                  {portalStatus.portalUser.lastLoginAt && (
                    <p>Last login: {formatDate(portalStatus.portalUser.lastLoginAt)}</p>
                  )}
                </div>
              )}

              {/* Send Invitation button (T145) */}
              {(portalStatus.status === 'NOT_INVITED' ||
                portalStatus.status === 'INVITATION_PENDING') && (
                <button
                  onClick={() => sendInvitation.mutate(customer.id)}
                  disabled={sendInvitation.isPending}
                  className="claude-button claude-button-primary w-full justify-center gap-2 text-sm mt-2"
                >
                  <Send className="w-4 h-4" />
                  {sendInvitation.isPending
                    ? 'Sending...'
                    : portalStatus.status === 'INVITATION_PENDING'
                      ? 'Resend Invitation'
                      : 'Send Portal Invitation'}
                </button>
              )}
              {sendInvitation.isSuccess && (
                <p className="text-sm text-success flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Invitation sent!
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">No portal access configured</p>
              <button
                onClick={() => sendInvitation.mutate(customer.id)}
                disabled={sendInvitation.isPending}
                className="claude-button claude-button-primary w-full justify-center gap-2 text-sm"
              >
                <Send className="w-4 h-4" />
                {sendInvitation.isPending ? 'Sending...' : 'Send Portal Invitation'}
              </button>
              {sendInvitation.isSuccess && (
                <p className="text-sm text-success flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Invitation sent!
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
